<script lang="ts">
  import { onMount, type Snippet } from 'svelte';
  import { afterNavigate, goto } from '$app/navigation';
  import { page } from '$app/state';

  import { DataTable, Pagination, Loader, Title } from '$shared/components';
  import { CursorFilterKey, PaginationFilterKey } from '$provider/constants/global';
  import { constructQueryParam } from '$provider/constants/Route';

  let dataTableEle: any = $state();

  interface Props {
    loading?: boolean;
    nopad?: boolean;
    simplePaging?: boolean;
    split?: boolean;
    hasSelectableRow?: boolean;
    hasOptions?: boolean;
    selectedRows?: Set<string>;
    disabledRows?: Set<string>;
    columns?: any;
    rows?: any;
    title?: string;
    overflow?: boolean;
    mainTitle?: string;
    pagination?: PaginationType | {};
    hideContextMenu?: boolean;
    hasPageNumberState?: boolean;
    haveSelectAll?: boolean;
    showSelectableAction?: boolean;
    getRowOptions?: any;
    name?: string;
    multiSorting?: boolean;
    rowClickActionDisabled?: boolean;
    disableUnselectedCheckbox?: boolean;
    titleAction?: Snippet | undefined;
    subTitle?: Snippet | undefined;
    headerBottom?: Snippet | undefined;
    selectableAction?: Snippet | undefined;
    filter?: Snippet | undefined;
    onmount?: (payload: { pageNumber: number; cursor: string }) => void;
    onroutechange?: () => void;
    onprevious?: () => void;
    onnext?: () => void;
    onsort?: (payload: any) => void;
    onevent?: (payload: any) => void;
    onclick?: (payload: any) => void;
    onchecked?: (payload: any) => void;
    oncontextmenu?: (payload: any) => void;
    onclickoption?: (payload: any) => void;
    onselectall?: (payload: boolean) => void;
  }

  let {
    loading = false,
    nopad = false,
    simplePaging = false,
    split = false,
    hasSelectableRow = false,
    hasOptions = false,
    selectedRows = new Set(),
    disabledRows = new Set(),
    columns = [],
    rows = [],
    title = '',
    overflow = false,
    mainTitle = '',
    pagination = {},
    hideContextMenu = false,
    hasPageNumberState = false,
    haveSelectAll = false,
    showSelectableAction = false,
    getRowOptions = () => {},
    name = '',
    multiSorting = false,
    rowClickActionDisabled = false,
    disableUnselectedCheckbox = false,
    titleAction = undefined,
    subTitle = undefined,
    headerBottom = undefined,
    selectableAction = undefined,
    filter = undefined,
    onmount,
    onroutechange,
    onprevious,
    onnext,
    onsort,
    onevent,
    onclick,
    onchecked,
    oncontextmenu,
    onclickoption,
    onselectall
  }: Props = $props();

  export const resetSort = () => {
    dataTableEle?.resetSort();
  };

  let hasPageParam = $derived(page.url.searchParams.has(PaginationFilterKey));
  let hasCursorParam = $derived(page.url.searchParams.has(CursorFilterKey));

  onMount(() => {
    loadPageNumberState();
    dispatchMountEvent();
  });

  afterNavigate(() => {
    onRouteChange();
  });

  const dispatchMountEvent = () => {
    const pageNumber = hasPageParam ? Number(page.url.searchParams.get(PaginationFilterKey)) : 0;
    const cursor = hasCursorParam ? page.url.searchParams.get(CursorFilterKey) : '';
    onmount?.({ pageNumber, cursor });
  };

  const onRouteChange = () => {
    onroutechange?.();
    loadPageNumberState();
  };

  const loadPageNumberState = () => {
    if (hasPageParam) {
      return;
    }
    updateLocStateWithPageNumber(1, '');
  };

  const onClickPrevious = () => {
    onprevious?.();
    const currentPage = pagination.currentPage - 1;
    updateLocStateWithPageNumber(currentPage, (currentPage > 1 && pagination.previousCursor) || '');
  };

  const onClickNext = () => {
    onnext?.();
    const currentPage = pagination.currentPage + 1;
    updateLocStateWithPageNumber(currentPage, (currentPage > 1 && pagination.nextCursor) || '');
  };

  const updateLocStateWithPageNumber = (pageNumber: number, cursor: string): void => {
    if (!hasPageNumberState) {
      return;
    }
    const queryParam = constructQueryParam({
      [PaginationFilterKey]: pageNumber,
      [CursorFilterKey]: cursor
    });
    goto(`${page.url.pathname}${queryParam}`, { replaceState: true });
  };
</script>

<div class="table-layout" class:overflow class:nopad>
  <div class="head">
    {#if mainTitle || title}
      <div data-cy={`${name}-title`} class="title">
        {#if loading}
          <div class="loader"><Loader count={1} /></div>
        {:else if mainTitle}
          <Title type="h2">{mainTitle}</Title>
        {:else}
          {title}
        {/if}
        <div class="title-action">{@render titleAction?.()}</div>
      </div>
      <div class="sub-title">
        {@render subTitle?.()}
      </div>
    {/if}
    <div class="filters" class:split>
      <div class="options">{@render filter?.()}</div>
      <div class="pagination" data-cy="providers-pagination">
        <Pagination
          simple={simplePaging}
          {...pagination}
          {loading}
          onprevious={onClickPrevious}
          onnext={onClickNext}
        />
      </div>
    </div>
    {@render headerBottom?.()}
  </div>
  <div class="table" data-cy="providers-data-table">
    <DataTable
      bind:this={dataTableEle}
      {loading}
      {columns}
      {rows}
      {name}
      {multiSorting}
      {hasOptions}
      {hideContextMenu}
      {hasSelectableRow}
      {haveSelectAll}
      {selectedRows}
      {disabledRows}
      {getRowOptions}
      {showSelectableAction}
      {overflow}
      {rowClickActionDisabled}
      {disableUnselectedCheckbox}
      {selectableAction}
      {onsort}
      {onclick}
      {onchecked}
      {oncontextmenu}
      {onclickoption}
      {onselectall}
      {onevent}
    />
  </div>
</div>

<style>
  .table-layout {
    padding: 16px;
    background-color: #fff;
  }

  .table-layout.overflow .table {
    overflow: auto;
  }

  .table-layout.nopad {
    padding: 0;
  }

  .title {
    font-weight: bold;
    font-size: 16px;
    margin-bottom: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .sub-title {
    margin-bottom: 16px;
  }

  .filters {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  .filters.split {
    border-bottom: 1px solid rgba(0, 0, 0, 0.07);
  }

  .options {
    flex-grow: 1;
    flex-basis: 70%;
  }
  .loader {
    width: 200px;
  }
</style>
